import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Button,
  Space,
  Progress,
  Table,
  Divider,
  Alert,
  Badge,
  Modal,
  Radio,
  message,
  Spin,
  Empty,
} from 'antd';
import {
  CrownOutlined,
  CheckCircleOutlined,
  RiseOutlined,
  TeamOutlined,
  DatabaseOutlined,
  MailOutlined,
  ThunderboltOutlined,
  SafetyOutlined,
  ApiOutlined,
  CloudOutlined,
} from '@ant-design/icons';
import { subscriptionApi } from '../../services/subscription';
import type {
  PlanWithQuota,
  Subscription as SubscriptionType,
  UsageSummary,
} from '../../types/subscription';
import { parseFeatures } from '../../types/subscription';
import { COLORS } from '../../constants/colors';

const { Title, Text, Paragraph } = Typography;

// 功能图标映射
const featureIcons: Record<string, React.ReactNode> = {
  proposal_agent: <CheckCircleOutlined />,
  scout_agent: <CheckCircleOutlined />,
  outreach_agent: <MailOutlined />,
  ppt_generation: <ThunderboltOutlined />,
  whatsapp: <MailOutlined />,
  team_management: <TeamOutlined />,
  api_access: <ApiOutlined />,
  private_deployment: <CloudOutlined />,
};

// 功能名称映射
const featureNames: Record<string, string> = {
  proposal_agent: '提案生成代理',
  scout_agent: '红人星探代理',
  outreach_agent: '邀约代理',
  ppt_generation: 'PPT自动生成',
  whatsapp: 'WhatsApp集成',
  team_management: '团队管理',
  api_access: 'API访问',
  private_deployment: '私有部署',
};

export const Subscription: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionType | null>(null);
  const [plans, setPlans] = useState<PlanWithQuota[]>([]);
  const [usageSummary, setUsageSummary] = useState<UsageSummary[]>([]);
  const [upgradeModalVisible, setUpgradeModalVisible] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PlanWithQuota | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annually'>('monthly');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [plansRes, subRes, usageRes] = await Promise.all([
        subscriptionApi.comparePlans(),
        subscriptionApi.getCurrentSubscription().catch(() => null),
        subscriptionApi.getUsageSummary().catch(() => ({ data: [] })),
      ]);

      if (plansRes.data) {
        setPlans(plansRes.data);
      }
      if (subRes?.data) {
        setSubscription(subRes.data);
      }
      if (usageRes && 'data' in usageRes && usageRes.data) {
        setUsageSummary(usageRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch subscription data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 计算试用剩余天数
  const getTrialDaysLeft = () => {
    if (!subscription?.trial_end) return 0;
    const trialEnd = new Date(subscription.trial_end);
    const now = new Date();
    const diffTime = trialEnd.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  // 获取进度条颜色
  const getProgressStatus = (percentage: number) => {
    if (percentage >= 100) return 'exception';
    if (percentage >= 80) return 'normal';
    return 'normal';
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 100) return COLORS.error;
    if (percentage >= 80) return COLORS.warning;
    return COLORS.primary;
  };

  // 打开升级弹窗
  const handleUpgradeClick = (plan: PlanWithQuota) => {
    if (plan.is_current) return;
    setSelectedPlan(plan);
    setBillingCycle('monthly');
    setUpgradeModalVisible(true);
  };

  // 执行升级/降级
  const handleConfirmAction = async () => {
    if (!selectedPlan) return;

    setActionLoading(true);
    try {
      const currentPlan = plans.find(p => p.is_current);
      const isUpgrade = currentPlan && selectedPlan.sort_order > currentPlan.sort_order;

      if (isUpgrade) {
        await subscriptionApi.upgrade({
          plan_id: selectedPlan.id,
          billing_cycle: billingCycle,
        });
        message.success('升级成功！');
      } else {
        await subscriptionApi.downgrade({
          plan_id: selectedPlan.id,
        });
        message.success('降级成功！');
      }

      setUpgradeModalVisible(false);
      fetchData();
    } catch (error) {
      console.error('Failed to change plan:', error);
    } finally {
      setActionLoading(false);
    }
  };

  // 渲染版本卡片
  const renderPlanCard = (plan: PlanWithQuota) => {
    const features = parseFeatures(plan.features);
    const price = billingCycle === 'annually' ? plan.price_annually : plan.price_monthly;
    const isEnterprise = plan.code === 'enterprise';

    return (
      <Col span={6} key={plan.id}>
        <Card
          size="small"
          title={
            <div style={{ textAlign: 'center' }}>
              <Text strong style={{ fontSize: 16 }}>{plan.name}</Text>
              {plan.is_current && (
                <div>
                  <Tag color="blue" style={{ marginTop: 4 }}>当前使用</Tag>
                </div>
              )}
            </div>
          }
          style={{
            height: '100%',
            borderColor: plan.is_current ? COLORS.primary : undefined,
            borderWidth: plan.is_current ? 2 : 1,
          }}
          bodyStyle={{ display: 'flex', flexDirection: 'column', minHeight: 280 }}
        >
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            {isEnterprise ? (
              <Title level={4} style={{ margin: 0 }}>联系销售</Title>
            ) : (
              <>
                <Title level={3} style={{ margin: 0, color: COLORS.primary }}>
                  ¥{price}
                </Title>
                <Text type="secondary">/{billingCycle === 'annually' ? '年' : '月'}</Text>
              </>
            )}
          </div>
          <Divider style={{ margin: '12px 0' }} />
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, flex: 1 }}>
            {Object.entries(features).map(([key, value]) => (
              value && (
                <li key={key} style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CheckCircleOutlined style={{ color: COLORS.success }} />
                  <Text style={{ fontSize: 13 }}>{featureNames[key] || key}</Text>
                </li>
              )
            ))}
          </ul>
          <Button
            type={plan.is_current ? 'default' : 'primary'}
            block
            style={{ marginTop: 16 }}
            disabled={plan.is_current}
            onClick={() => handleUpgradeClick(plan)}
          >
            {plan.is_current ? '当前版本' : isEnterprise ? '联系销售' : '选择版本'}
          </Button>
        </Card>
      </Col>
    );
  };

  // 用量统计卡片内容
  const renderUsageContent = () => {
    if (usageSummary.length === 0) {
      return <Empty description="暂无用量数据" />;
    }

    return (
      <Row gutter={[24, 24]}>
        {usageSummary.map((item) => (
          <Col span={8} key={item.metric_type}>
            <div style={{ marginBottom: 8 }}>
              <Text>{item.display_name}</Text>
              <Text type="secondary" style={{ float: 'right' }}>
                {item.is_unlimited ? (
                  <span>{item.used} / 无限</span>
                ) : (
                  `${item.used} / ${item.limit}`
                )}
              </Text>
            </div>
            {item.is_unlimited ? (
              <Progress percent={100} size="small" showInfo={false} strokeColor={COLORS.success} />
            ) : (
              <Progress
                percent={Math.min(item.percentage, 100)}
                size="small"
                status={getProgressStatus(item.percentage)}
                strokeColor={getProgressColor(item.percentage)}
              />
            )}
          </Col>
        ))}
      </Row>
    );
  };

  return (
    <Spin spinning={loading}>
      <div>
        <Title level={4} style={{ marginBottom: 24 }}>
          订阅管理
        </Title>

        {/* 当前订阅信息 */}
        <Card style={{ marginBottom: 24 }}>
          <Row gutter={24} align="middle">
            <Col span={16}>
              <Space align="center" size="large">
                <CrownOutlined style={{ fontSize: 40, color: subscription?.is_trialing ? COLORS.warning : COLORS.primary }} />
                <div>
                  <Title level={4} style={{ margin: 0 }}>
                    {subscription?.plan?.name || '加载中...'}
                    {subscription?.is_trialing && (
                      <Tag color="orange" style={{ marginLeft: 8 }}>试用中</Tag>
                    )}
                  </Title>
                  <Space style={{ marginTop: 8 }}>
                    <Text type="secondary">
                      {subscription?.is_trialing ? (
                        `试用到期：${subscription.trial_end ? new Date(subscription.trial_end).toLocaleDateString() : '-'}`
                      ) : (
                        `到期时间：${subscription?.end_date ? new Date(subscription.end_date).toLocaleDateString() : '-'}`
                      )}
                    </Text>
                    {!subscription?.is_trialing && (
                      <Text type="secondary">
                        | 自动续费：{subscription?.auto_renew ? '已开启' : '已关闭'}
                      </Text>
                    )}
                  </Space>
                </div>
              </Space>

              {/* 试用提示 */}
              {subscription?.is_trialing && (
                <Alert
                  message={`您正在使用专业版试用，还剩 ${getTrialDaysLeft()} 天`}
                  type="info"
                  showIcon
                  style={{ marginTop: 16 }}
                  action={
                    <Button size="small" type="primary" onClick={() => {
                      const professionalPlan = plans.find(p => p.code === 'professional');
                      if (professionalPlan) handleUpgradeClick(professionalPlan);
                    }}>
                      立即升级
                    </Button>
                  }
                />
              )}
            </Col>
            <Col span={8} style={{ textAlign: 'right' }}>
              {subscription?.plan && subscription.plan.code !== 'enterprise' && (
                <>
                  <Title level={3} style={{ margin: 0, color: COLORS.primary }}>
                    ¥{subscription.price}
                    <Text type="secondary" style={{ fontSize: 14 }}>
                      /{subscription.billing_cycle === 'annually' ? '年' : '月'}
                    </Text>
                  </Title>
                </>
              )}
            </Col>
          </Row>
        </Card>

        {/* 用量统计 */}
        <Card title="本月用量" style={{ marginBottom: 24 }}>
          {renderUsageContent()}
        </Card>

        {/* 版本对比 */}
        <Card 
          title="版本对比"
          style={{ marginBottom: 24 }}
          extra={
            <Radio.Group value={billingCycle} onChange={(e) => setBillingCycle(e.target.value)}>
              <Radio.Button value="monthly">月付</Radio.Button>
              <Radio.Button value="annually">年付(享8折)</Radio.Button>
            </Radio.Group>
          }
        >
          <Row gutter={16}>
            {plans.map(renderPlanCard)}
          </Row>
        </Card>

        {/* 升级/降级确认弹窗 */}
        <Modal
          title={selectedPlan && plans.find(p => p.is_current)?.sort_order! < selectedPlan.sort_order ? '确认升级' : '确认降级'}
          open={upgradeModalVisible}
          onCancel={() => setUpgradeModalVisible(false)}
          onOk={handleConfirmAction}
          confirmLoading={actionLoading}
        >
          {selectedPlan && (
            <div>
              <Paragraph>
                您确定要{plans.find(p => p.is_current)?.sort_order! < selectedPlan.sort_order ? '升级' : '降级'}到 
                <Text strong> {selectedPlan.name} </Text>吗？
              </Paragraph>
              {billingCycle === 'annually' ? (
                <Paragraph>
                  年付价格：<Text strong style={{ fontSize: 18, color: COLORS.primary }}>¥{selectedPlan.price_annually}</Text>/年
                </Paragraph>
              ) : (
                <Paragraph>
                  月付价格：<Text strong style={{ fontSize: 18, color: COLORS.primary }}>¥{selectedPlan.price_monthly}</Text>/月
                </Paragraph>
              )}
            </div>
          )}
        </Modal>
      </div>
    </Spin>
  );
};
